import type { ExternalDeliveryStatus } from "utils/status"

export interface RouteDriver {
	_id: string
	name: string
}

export type Driver = {
	_id: string
	name: string
	role?: string
	photo?: string
}

export interface RouteAttachment {
	url: string
}

export interface NotDeliveredReason {
	id: string
	externalCode: string
	reason: string
}

export type AddressStatus =
	| "on_transit"
	| "delivered"
	| "not_delivered"
	| "cancelled"
	| "returned"
	| "dispute"
	| "billing"

export interface StatusFlowEvent {
	status: ExternalDeliveryStatus
	timestamp: { $date: string } | string
	location: { lat: number; lng: number } | null
}

export interface OrderExternalDelivery {
	partner: string
	batchId?: string
	internalBatchId?: string
	status?: string
	courier?: {
		name: string
		phoneNumber?: string
		vehicleType?: string
		vehicleColor?: string
		vehicleMake?: string
		vehicleModel?: string
		location?: { lat: number; lng: number }
	}
	pickupEta?: string
	dropoffEta?: string
	trackingUrl?: string
	returnPincode?: string
	undeliverableAction?: string
	undeliverableReason?: string
	returnDeliveryId?: string // Unset: the partner is not bringing the parcel back
	returnStatus?: "returning" | "returned" | "failed"
	returned?: boolean
	returnedAt?: string
	pickupVerification?: { barcodes?: { type?: string; value?: string }[] }
	manifestItems?: { name?: string; quantity?: number }[]
	statusFlow?: StatusFlowEvent[]
}

export interface Order {
	orderId: string
	confirmedBarcode?: string
	status: AddressStatus
	arrivalTime?: string
	departureTime?: string
	notes?: string
	deliveredDate?: string
	deliveredLocation?: { lat: number; lng: number } | null
	notDeliveredReason?: string
	slot?: {
		from: number
		to: number
	}
	boxes?: Array<{ id: string; treated: boolean }>
	bagId?: string
	serviceType?: string
	bundledOrders?: Order[]
	fleetTaskId?: string
	externalDelivery?: OrderExternalDelivery
	user: {
		name: string
		phone: string
	}
}

export interface RouteAddress {
	address: string
	location: {
		lat: number
		lng: number
	}
	isDepot?: boolean
	order: Order
	orderId?: string
	order_id?: string
	stopType?: string
	address_stop_type?: string
	serviceType?: string
	eta?: number
	etd?: number
}

export const ROUTE_STATUS_VALUES = [
	"loading",
	"aggregation",
	"ready_to_van",
	"dispatching",
	"on_transit",
	"finished",
] as const

export const ORDER_COMPLETION_STATUSES: AddressStatus[] = [
	"delivered",
	"not_delivered",
	"cancelled",
	"returned",
	"dispute",
	"billing",
]

export const ORDER_FAILED_STATUSES: AddressStatus[] = [
	"not_delivered",
	"cancelled",
	"returned",
	"dispute",
]

export type RouteStatus = (typeof ROUTE_STATUS_VALUES)[number]

export type RouteType = "van" | "bike" | "fr"

export type Grid = {
	_id: string
	name: string
	disabled?: boolean
}

export type Vehicle = {
	_id: string
	name: string
	licensePlate: string
	fleetVehicleId?: string
	type: string
	capacity: number
}

export interface GridLoadSequenceItem {
	orderId?: {
		$oid: string
	}
	optimizerOrderId?: string
	boxId?: string
	boxWeight?: number
	line?: number
	column?: number
	depth?: number
	position?: number | string
	pile?: number | string
}

export interface OutOfFormatBox {
	boxId: string
}

export interface RouteExternalDeliveryBatch {
	internalBatchId: string
	batchId?: string
	shortBatchId?: string
	status?: "waiting_for_batching" | "batched" | "in_progress" | "completed"
}

export interface RouteExternalDelivery {
	partner: string
	assignedAt?: string
	assignedBy?: string
	batches?: RouteExternalDeliveryBatch[]
}

export type LocalizedMessage = {
	en?: string
	pt?: string
	[lang: string]: string | undefined
}

export type ExternalEligibilityStatus =
	| "assigned"
	| "eligible"
	| "warning"
	| "ineligible"

export interface RouteExternalEligibility {
	status: ExternalEligibilityStatus
	/** The partner that accepts the route (e.g. "uber") when eligible, else null. */
	partner?: string | null
	/** Order tallies — fully correct vs flagged with a warning. */
	counts?: { ok: number; warning: number }
	/** Orders flagged with a warning (landline / no phone). */
	warningOrders?: { orderId: string; name?: string | null }[]
	reason?: {
		code: string
		message: LocalizedMessage
	} | null
}

export interface RoutePickup {
	type: "warehouse" | "van"
	sequenceNr?: number | null
	vanVehicleId?: number | null
	vanRouteName?: string | null
	address?: string | null
}

export interface Route {
	_id: string
	name: string
	systemName: string
	routeNameNumber: number
	vehicleId: number
	date: string
	gradeId: string
	gridName: string
	contract?: string
	contractName?: string
	vehicle?: string
	vehicleInfo?: Vehicle
	evalyzeRouteId: string
	addresses: RouteAddress[]
	gridLoadSequence?: GridLoadSequenceItem[]
	boxesOutOfFormat?: OutOfFormatBox[]
	routeType: RouteType
	driver: string
	driverInfo?: Driver
	status: RouteStatus
	startedRouteDate?: string
	endedRouteDate?: string
	attachments: RouteAttachment[]
	externalDelivery?: RouteExternalDelivery
	externalEligibility?: RouteExternalEligibility
	pickup?: RoutePickup
	alreadyTraveledPath?: { lat: number; lng: number }[]
	googleRoutePolyline?: string
}

export interface RoutesFilters {
	status?: RouteStatus[]
	search?: string
}
